import React from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {usePage} from '@salesforce/commerce-sdk-react'
import {Page} from '@salesforce/commerce-sdk-react/components'
import {
    PDCLink,
    PDCHomeServicesPushTile,
    PDCHomeCategoriesPushTile,
    PDCInnerAccordion,
    PDCBlogArticlesList,
    PDCHomeSEO,
    PDCCarouselImageGalleryTile
} from '../../page-designer/assets'
import {
    PDCHomeDyptich,
    PDCHomeFullScreenPushImage,
    PDCHomeFullScreenPushVideo,
    PDCHomeProductDyptichVideo,
    PDCHomeProductDyptichImage,
    PDCHomeServicesPush,
    PDCHomeCategoriesPush,
    PDCAboutTextBlock,
    PDCButton,
    PDCTitlePageOnHeaderImage,
    PDCAnchorsBlock,
    PDCAccordionBlock,
    PDCAccordion,
    PDCAboutStoryBlockDefine,
    PDCAboutStoryBlockExcerpt,
    PDCHomeHeroArticle,
    PDCClassicProductCarousel,
    PDCHomeAboutPush,
    PDCHomePatternFinderPush,
    PDCCarouselImageGallery,
    PDCAboutProductSlider
} from '../../page-designer/layouts'

// Constants
import {MAX_CACHE_AGE_STATIC_PAGES, STALE_WHILE_REVALIDATE} from '../../constants'

import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

// Utils
import {getEnvStaleTime} from '../../utils/utils'

// Custom hooks
import useMultiSite from '../../hooks/use-multi-site'
import {useGTM} from '@app/hooks/use-gtm'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcLink': PDCLink,
    'commerce_assets.pdcDisplayConfigLink': PDCLink,
    'commerce_assets.pdcHomeServicesPushTile': PDCHomeServicesPushTile,
    'commerce_assets.pdcHomeCategoriesPushTile': PDCHomeCategoriesPushTile,
    'commerce_assets.pdcInnerAccordion': PDCInnerAccordion,
    'commerce_assets.pdcBlogArticlesList': PDCBlogArticlesList,
    'commerce_assets.pdcHomeSeo': PDCHomeSEO,
    'commerce_assets.pdcCarouselImageGalleryTile': PDCCarouselImageGalleryTile,
    'commerce_layouts.pdcHomeDyptich': PDCHomeDyptich,
    'commerce_layouts.pdcHomeFullScreenPushImage': PDCHomeFullScreenPushImage,
    'commerce_layouts.pdcHomeFullScreenPushVideo': PDCHomeFullScreenPushVideo,
    'commerce_layouts.pdcHomeProductDyptichVideo': PDCHomeProductDyptichVideo,
    'commerce_layouts.pdcHomeProductDyptichImage': PDCHomeProductDyptichImage,
    'commerce_layouts.pdcHomeServicesPush': PDCHomeServicesPush,
    'commerce_layouts.pdcHomeCategoriesPush': PDCHomeCategoriesPush,
    'commerce_layouts.pdcAboutTextBlock': PDCAboutTextBlock,
    'commerce_layouts.pdcButton': PDCButton,
    'commerce_layouts.pdcTitlePageOnHeaderImage': PDCTitlePageOnHeaderImage,
    'commerce_layouts.pdcAnchorsBlock': PDCAnchorsBlock,
    'commerce_layouts.pdcAccordionBlock': PDCAccordionBlock,
    'commerce_layouts.pdcAccordion': PDCAccordion,
    'commerce_layouts.pdcAboutStoryBlockDefine': PDCAboutStoryBlockDefine,
    'commerce_layouts.pdcAboutStoryBlockExcerpt': PDCAboutStoryBlockExcerpt,
    'commerce_layouts.pdcHomeHeroArticle': PDCHomeHeroArticle,
    'commerce_layouts.pdcClassicProductCarousel': PDCClassicProductCarousel,
    'commerce_layouts.pdcHomeAboutPush': PDCHomeAboutPush,
    'commerce_layouts.pdcHomePatternFinderPush': PDCHomePatternFinderPush,
    'commerce_layouts.pdcCarouselImageGallery': PDCCarouselImageGallery,
    'commerce_layouts.pdcAboutProductSlider': PDCAboutProductSlider
}

const Home = () => {
    const staleTime = getEnvStaleTime()
    const {site} = useMultiSite()

    const getHomePageId = () => {
        const siteId = `${site?.id}`.toLowerCase()
        return `${siteId}-homepage`
    }
    const {data: page, error} = usePage(
        {parameters: {pageId: getHomePageId()}},
        {staleTime: staleTime}
    )

    // useServerContext is a special hook introduced in v3 PWA Kit SDK.
    // It replaces the legacy `getProps` and provide a react hook interface for SSR.
    // it returns the request and response objects on the server side,
    // and these objects are undefined on the client side.
    const {res} = useServerContext()

    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_STATIC_PAGES}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    if (error) {
        let ErrorClass = error.response?.status === 404 ? HTTPNotFound : HTTPError
        throw new ErrorClass(error.response?.statusText)
    }

    return (
        <>
            <Box layerStyle={'page'} css={[Home.css]}>
                <Page page={page} components={PAGEDESIGNER_TO_COMPONENT} />
            </Box>
        </>
    )
}

Home.css = css`
    // Avoid the header to overlap target component
    // when clicking on an anchor link
    .region > .container > .component {
        scroll-margin-top: 64px;

        @media (min-width: 768px) {
            scroll-margin-top: 52px;
        }
    }
`

Home.displayName = 'Home'

export default Home
